import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../DataStore/AuthContext';

const TYPE_MAP = { time: 'Time-based', data: 'Data-based', hybrid: 'Hybrid' };

const defaults = {
  name: '',
  type: 'time',                 // time | data | hybrid
  durationMinutes: '',          // minutes
  quotaMB: '',                  // megabytes
  speedKbps: '',                // single cap for both directions
  price: '',
  currency: 'UGX',
  maxDevices: 1,
  expiresAfterHours: '',        // optional; passthrough (not in DB by default)
  description: '',
  sort: 0,
  portalVisible: true,
  active: true,
};

const toInt = (v, d = null) => {
  if (v === '' || v === null || typeof v === 'undefined') return d;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? d : n;
};

const toMbpsFromKbps = (kbps) => {
  const n = toInt(kbps, 0);
  if (n <= 0) return null;
  // Approx: 1000 Kbps ≈ 1 Mbps (keep integers for backend)
  return Math.max(1, Math.round(n / 1000));
};

const toKbpsFromMbps = (mbps) => {
  const n = toInt(mbps, 0);
  if (n <= 0) return null;
  return n * 1000;
};

const WifiPlanForm = ({
  editingId = null,
  initialValues, // IMPORTANT: no default {} to avoid changing ref every render
  onSubmit,                         // async (payload, {mode, id}) => { ok:boolean, message?:string }
}) => {
  const {connectedProfiles} = useAuth();
  // Initialize once; merge in initialValues if provided
  const [form, setForm] = useState(() => ({ ...defaults, ...(initialValues ?? {}) }));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // {type:'success'|'error', msg:string}


  // Only update state when initialValues reference changes & is defined
  useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? !!checked : value,
    }));
  };

  const resetForm = () => {
    setForm({ ...defaults });
    setStatus(null);
  };

  const preview = useMemo(() => {
    const price = Number(form.price) || 0;
    const quotaGB = form.quotaMB ? Number(form.quotaMB) / 1024 : 0;
    const hours = form.durationMinutes ? Number(form.durationMinutes) / 60 : 0;
    const pricePerGB = quotaGB > 0 ? Math.round(price / quotaGB) : null;
    const pricePerHour = hours > 0 ? Math.round(price / hours) : null;
    return { pricePerGB, pricePerHour };
  }, [form.price, form.quotaMB, form.durationMinutes]);

  const buildPayload = () => {
    const planType = TYPE_MAP[form.type] || 'Time-based';
    const duration_minutes = form.type !== 'data' ? toInt(form.durationMinutes, null) : null;
    const data_limit_mb = form.type !== 'time' ? toInt(form.quotaMB, null) : null;
    const is_unlimited = duration_minutes === null && data_limit_mb === null ? 1 : 0;
    const mbps = toMbpsFromKbps(form.speedKbps);

    return {
      plan_name: String(form.name || '').trim(),
      price: toInt(form.price, 0) ?? 0,
      description: String(form.description || '').trim(),
      attrs: {
        currency: form.currency || 'UGX',
        sort_order: toInt(form.sort, 0) ?? 0,
        is_visible: form.portalVisible ? 1 : 0,
        status: form.active ? 'active' : 'inactive',
        plan_type: planType,
        duration_minutes,
        data_limit_mb,
        is_unlimited,
        shared_users: Math.max(1, toInt(form.maxDevices, 1) ?? 1),
        concurrent_sessions: Math.max(1, toInt(form.maxDevices, 1) ?? 1),
        download_mbps: mbps,
        upload_mbps: mbps,
        // passthrough (not persisted unless backend supports it)
        voucher_expiry_hours: toInt(form.expiresAfterHours, null),
      },
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    // Basic checks
    if (!form.name?.trim()) {
      setStatus({ type: 'error', msg: 'Plan name is required.' });
      return;
    }
    if (form.price === '' || Number(form.price) < 0) {
      setStatus({ type: 'error', msg: 'Price must be zero or more.' });
      return;
    }

    const payload = buildPayload();

    try {
      setSaving(true);

      if (typeof onSubmit === 'function') {
        const res = await onSubmit(payload, { mode: editingId ? 'update' : 'create', id: editingId });
        if (res && res.ok) {
          setStatus({ type: 'success', msg: res.message || (editingId ? 'Plan updated.' : 'Plan created.') });
          if (!editingId) resetForm();
        } else {
          setStatus({ type: 'error', msg: (res && res.message) || 'Operation failed.' });
        }
        return;
      }

    } catch (err) {
      setStatus({ type: 'error', msg: err?.message || 'Unexpected error.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="row">
      <div className="col-lg-8">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="card-title mb-1">{editingId ? 'Edit WiFi Plan' : 'Create WiFi Plan'}</h5>
                <p className="text-muted mb-0">Define limits, price and visibility.</p>
              </div>
              {status?.msg && (
                <div className={`alert mb-0 ${status.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                  {status.msg}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group col-md-8">
                  <label htmlFor="name">Plan Name</label>
                  <input id="name" name="name" className="form-control" value={form.name} onChange={onChange} placeholder="e.g. 1 Hour Unlimited / 1 GB" required />
                </div>
                <div className="form-group col-md-4">
                  <label htmlFor="type">Type</label>
                  <select id="type" name="type" className="form-control" value={form.type} onChange={onChange}>
                    <option value="time">Time-based</option>
                    <option value="data">Data-based</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </div>

              {/* Limits */}
              <div className="form-row">
                {(form.type === 'time' || form.type === 'hybrid') && (
                  <div className="form-group col-md-4">
                    <label htmlFor="durationMinutes">Duration (minutes)</label>
                    <input type="number" min="1" id="durationMinutes" name="durationMinutes" className="form-control" value={form.durationMinutes} onChange={onChange} placeholder="e.g. 60" />
                  </div>
                )}
                {(form.type === 'data' || form.type === 'hybrid') && (
                  <div className="form-group col-md-4">
                    <label htmlFor="quotaMB">Data Quota (MB)</label>
                    <input type="number" min="1" id="quotaMB" name="quotaMB" className="form-control" value={form.quotaMB} onChange={onChange} placeholder="e.g. 1024" />
                  </div>
                )}
                <div className="form-group col-md-4">
                  <label htmlFor="speedKbps">Speed Cap (Kbps) <span className="text-muted">(optional)</span></label>
                  <input type="number" min="0" id="speedKbps" name="speedKbps" className="form-control" value={form.speedKbps} onChange={onChange} placeholder="e.g. 2048" />
                </div>
              </div>

              {/* Pricing & access */}
              <div className="form-row">
                <div className="form-group col-md-4">
                  <label htmlFor="price">Price ({connectedProfiles?.currency || 'UGX'})</label>
                  <input type="number" min="0" id="price" name="price" className="form-control" value={form.price} onChange={onChange} placeholder="e.g. 2000" />
                </div>
                <div className="form-group col-md-4">
                  <label htmlFor="maxDevices">Max Devices</label>
                  <input type="number" min="1" id="maxDevices" name="maxDevices" className="form-control" value={form.maxDevices} onChange={onChange} />
                </div>
                {/* <div className="form-group col-md-4">
                  <label htmlFor="expiresAfterHours">Voucher Expires In (hours)</label>
                  <input type="number" min="1" id="expiresAfterHours" name="expiresAfterHours" className="form-control" value={form.expiresAfterHours} onChange={onChange} />
                </div> */}
              </div>

              <div className="form-row">
                <div className="form-group col-md-8">
                  <label htmlFor="description">Description</label>
                  <input id="description" name="description" className="form-control" value={form.description} onChange={onChange} placeholder="Short label shown in the portal" />
                </div>
                <div className="form-group col-md-4">
                  <label htmlFor="sort">Sort Order</label>
                  <input type="number" id="sort" name="sort" className="form-control" value={form.sort} onChange={onChange} />
                </div>
              </div>

              <div className="d-flex align-items-center mb-3">
                <div className="custom-control custom-checkbox mr-4">
                  <input type="checkbox" className="custom-control-input" id="portalVisible" name="portalVisible" checked={form.portalVisible} onChange={onChange} />
                  <label className="custom-control-label" htmlFor="portalVisible">Show in Captive Portal</label>
                </div>
                <div className="custom-control custom-checkbox mr-4">
                  <input type="checkbox" className="custom-control-input" id="active" name="active" checked={form.active} onChange={onChange} />
                  <label className="custom-control-label" htmlFor="active">Active</label>
                </div>
              </div>

              {/* Preview / Calculator */}
              <div className="card bg-light border mb-3">
                <div className="card-body py-2 d-flex justify-content-between">
                  <div>
                    <strong>Preview:</strong> {form.name || '—'} <span className="text-muted">({form.type})</span>
                    {form.type === 'time' && form.durationMinutes ? <> • {form.durationMinutes} min</> : null}
                    {form.type === 'data' && form.quotaMB ? <> • {(Number(form.quotaMB)/1024).toFixed(1)} GB</> : null}
                    {form.type === 'hybrid' && (form.durationMinutes || form.quotaMB) ? <> • {(form.durationMinutes||0)} min / {(form.quotaMB? (Number(form.quotaMB)/1024).toFixed(1):0)} GB</> : null}
                    {form.speedKbps ? <> • up to {form.speedKbps} Kbps (~{toMbpsFromKbps(form.speedKbps) ?? 0} Mbps)</> : null}
                  </div>
                  <div className="text-right">
                    <div><strong>Price:</strong> {form.price ? Number(form.price).toLocaleString() : 0} {form.currency}</div>
                    <div className="small text-muted">
                      {preview.pricePerGB ? <>~ {preview.pricePerGB.toLocaleString()} UGX/GB</> : null}
                      {preview.pricePerHour ? <> {preview.pricePerGB ? '• ' : ''}~ {preview.pricePerHour.toLocaleString()} UGX/hr</> : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex">
                <button type="submit" className="btn btn-primary mr-2" disabled={saving}>
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                      Saving…
                    </>
                  ) : editingId ? 'Save Changes' : 'Create Plan'}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={resetForm} disabled={saving}>
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Side tips */}
      <div className="col-lg-4">
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-title mb-2">Tips</h6>
            <ul className="mb-0 pl-3">
              <li><strong>Hybrid</strong> ends when time OR data runs out.</li>
              <li>Use <em>Sort Order</em> to pin popular plans on top.</li>
              <li>Hide staff/test plans by unchecking <em>Show in Captive Portal</em>.</li>
            </ul>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h6 className="card-title mb-2">Good defaults</h6>
            <ul className="mb-0 pl-3">
              <li>1 Hour @ 2,000 UGX, 2 Mbps cap</li>
              <li>1 GB @ 3,000 UGX, 4 Mbps cap</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WifiPlanForm;
